import { useCallback, useEffect } from 'react';
import { SENTRY_CONTEXT_KEYS, setSentryContext } from '@/utils/sentry';
import { useSenderQuery } from './useSender';
import { useSenderCreate } from './useSenderCreate';
import { useSenderStore, useSenderStoreHydration } from './useSenderStore';
import { useWidgetConfig } from './useWidgetConfig';
import useWidgetDataAttr from './useWidgetDataAttr';

export const useSenderInit = () => {
  const { companyId } = useWidgetDataAttr();
  const { config: widgetConfig } = useWidgetConfig();
  const {
    senderId,
    actions: { setSenderId, setSignature, getStorageKey },
  } = useSenderStore();
  const hydrated = useSenderStoreHydration();

  // Create sender mutation
  const createSenderMutation = useSenderCreate({
    onSuccess: (data) => {
      setSenderId(data.id);
      setSignature(data.sessionSignature);
    },
  });
  const senderQuery = useSenderQuery({ retry: 0 });

  const autoCreateSender = useCallback(async () => {
    const storageKey = getStorageKey(
      widgetConfig?.channelIdentityId || '',
      companyId || '',
    );

    if (!widgetConfig?.channelIdentityId || !companyId) {
      return;
    }

    if (useSenderStore.persist.getOptions().name !== storageKey) {
      // To ensure two livechat widget will not have storage key collision
      useSenderStore.persist.setOptions({
        name: storageKey,
      });
      useSenderStore.persist.rehydrate();
      return;
    }

    if (!hydrated) {
      // wait until the senderId is hydrated
      return;
    }

    if (
      !senderId &&
      !createSenderMutation.isPending &&
      !createSenderMutation.isError
    ) {
      try {
        await createSenderMutation.mutateAsync({
          liveChatId: widgetConfig.channelIdentityId,
          companyId,
        });
      } catch (error) {
        console.error('Failed to auto-create sender:', error);
      }
    }
    // if sender not found, recreate a new sender
    if (senderQuery.isError && !createSenderMutation.isPending) {
      try {
        await createSenderMutation.mutateAsync({
          liveChatId: widgetConfig.channelIdentityId,
          companyId,
        });
        return;
      } catch (error) {
        console.error('Failed to re-create sender:', error);
      }
    }
  }, [
    getStorageKey,
    widgetConfig,
    companyId,
    hydrated,
    senderId,
    createSenderMutation,
    senderQuery,
  ]);

  useEffect(() => {
    if (senderId) {
      setSentryContext(SENTRY_CONTEXT_KEYS.SENDER, {
        senderId,
      });
    }
  }, [senderId]);

  // Auto-create sender when widget config is available and sender doesn't exist
  useEffect(() => {
    autoCreateSender();
  }, [autoCreateSender]);
};
